import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { paths } from "ui/routes";
import { Spinner, Button } from "ui/components";
import { useSessionContext } from "ui/context/SessionContext";
import { verifyUser, requestVerification } from "core/services/site/user";
import { getLoggedIn } from "core/services/authserver";
import styles from "./Verify.module.scss";

interface Params {
    [key: string]: string | undefined;
    token: string;
}
const Verify: React.FC = () => {
    const { token: verifyToken } = useParams<Params>();
    const [verified, setVerified] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>();
    const [tokenRequested, setTokenRequested] = React.useState<boolean>(false);
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    const verifiedTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleTokenRequest = async (): Promise<void> => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            const result = await requestVerification(
                sessionContext.session.token,
            );
            if (result.error) {
                throw new Error("Unable to request your verification");
            }
            setTokenRequested(true);
        } catch (err) {
            setError((err as Error).message);
        }
    };

    React.useEffect(() => {
        if (verified) {
            verifiedTimeoutRef.current = setTimeout(() => {
                navigate(paths.projectsPage);
            }, 1000);
        } else if (verifiedTimeoutRef.current) {
            clearTimeout(verifiedTimeoutRef.current);
        }
        return () => {
            if (verifiedTimeoutRef.current) {
                clearTimeout(verifiedTimeoutRef.current);
            }
        };
    }, [verified, navigate]);

    const handleVerify = async (): Promise<void> => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            if (!verifyToken) {
                throw Error("missing token");
            }
            const response = await verifyUser(
                verifyToken,
                sessionContext.session.token,
            );
            if (response.error || !response.value) {
                throw new Error(response.error);
            } else {
                const loggedinInfo = await getLoggedIn(
                    sessionContext.session.token,
                );
                if (loggedinInfo.error || !loggedinInfo.value) {
                    throw new Error(loggedinInfo.error);
                }
                sessionContext.setVerified(loggedinInfo.value.verified);
                setVerified(loggedinInfo.value.verified);
            }
        } catch (err) {
            setError((err as Error).message);
        }
    };

    React.useEffect(() => {
        if (verifyToken && sessionContext.session?.token) {
            handleVerify();
        }
    }, [verifyToken, sessionContext.session?.token]);

    return (
        <div className={styles.verifypage}>
            {verifyToken && !verified && !error && (
                <Spinner className={styles.spinner} size="medium" />
            )}
            <div className={styles.msg}>
                {!verifyToken && "No token was found"}
                {!verified && !error && "Processing verification"}
                {verified && "Account verified"}
                {error && !tokenRequested && (
                    <>
                        <p>{error}</p>
                        <p>your token may be expired</p>
                        <Button
                            type="secondary"
                            theme="light"
                            onClick={handleTokenRequest}
                        >
                            Request new token
                        </Button>
                    </>
                )}
                {tokenRequested && "Token Requested, check your email"}
            </div>
        </div>
    );
};

export default Verify;
